import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  type SubmitAppointmentRatingResponse,
  submitAppointmentRatingMutation,
} from '@/api/appointment/submitAppointmentRatingMutation';
import { customerSaleQueryOptions } from '@/api/customer/customerSaleQuery';
import type { CustomerSaleResponse } from '@/api/customer/customerSaleQuery/types';
import { useToast } from '@/components/_shared/Toaster';
import { useErrorTranslations } from '@/hooks/useErrorTranslations';
import { Logger } from '@/utils/logging';
import { optimisticUpdateHelper } from '@/utils/optimisticUpdates';
import { Tracker } from '@/utils/tracking';

import type {
  SubmitAppointmentRatingMutationContext,
  SubmitAppointmentRatingParams,
  UseSubmitAppointmentRatingMutationOptions,
} from './types';

// Updaters for CustomerSale query
const saleUpdaters = {
  updateRating: (
    old: CustomerSaleResponse,
    appointmentId: string,
    rating: number,
  ): CustomerSaleResponse => ({
    ...old,
    items: old.items.map((item) =>
      item.appointment?.id === appointmentId
        ? {
            ...item,
            appointment: { ...item.appointment, appointmentRating: rating },
          }
        : item,
    ),
  }),
};

/**
 * React Query mutation hook for submitting appointment ratings
 *
 * @param options - Mutation configuration options
 * @returns Mutation result with updated sale data
 */
export const useSubmitAppointmentRatingMutation = (
  options?: UseSubmitAppointmentRatingMutationOptions,
) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useErrorTranslations('appointment');

  return useMutation<
    SubmitAppointmentRatingResponse,
    Error,
    SubmitAppointmentRatingParams,
    SubmitAppointmentRatingMutationContext
  >({
    ...submitAppointmentRatingMutation(),
    onMutate: async (variables) => {
      // Create helper and pass through context
      const sale = optimisticUpdateHelper<CustomerSaleResponse>()(
        queryClient,
        customerSaleQueryOptions(variables.saleOrderIdOrAppointmentId).queryKey,
        saleUpdaters,
      );

      const previousCustomerSale = await sale.snapshot();
      sale.updateRating(variables.appointmentId, variables.appointmentRating);

      return { previousCustomerSale, sale };
    },
    onError: (error, variables, context, mutationContext) => {
      Logger.error('Error submitting appointment rating', { error, variables });

      // Use helper from context
      context?.sale?.rollback(context?.previousCustomerSale);

      const message =
        error instanceof Error || 'message' in error
          ? error.message
          : t('unknownError');

      toast({
        variant: 'error',
        message: t('ratingUpdateFailed'),
        description: message,
      });

      options?.onError?.(error, variables, context, mutationContext);
    },
    onSettled: (_data, _error, _variables, context) => {
      // Use helper from context
      context?.sale?.invalidate();
    },
    onSuccess: (data, variables, context, mutationContext) => {
      Tracker.track('appointment_rated', {
        appointmentId: variables.appointmentId,
        rating: variables.appointmentRating.toString(),
        saleOrderId: variables.saleOrderIdOrAppointmentId,
      });

      options?.onSuccess?.(data, variables, context, mutationContext);
    },
    retry: false,
    ...options,
  });
};
